import React, { useEffect, useState, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Alert,
  Tag,
  Input,
} from 'antd';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet';
import { SearchOutlined } from '@ant-design/icons';
import api from '../../services/api';

const SubscribedUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Search functionality states
  const searchInput = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  const clearMessages = () => {
    setSuccessMsg('');
    setErrorMsg('');
  };

  // Search functions
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
          autoFocus
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => {
      if (searchedColumn === dataIndex && searchText) {
        const regex = new RegExp(searchText, 'gi');
        const parts = String(text).split(regex);
        return (
          <>
            {parts.map((part, index) =>
              index > 0 ? (
                <span key={index} style={{ backgroundColor: '#ffc069' }}>
                  {searchText}
                </span>
              ) : (
                part
              )
            )}
          </>
        );
      }
      return text;
    },
  });

  const fetchSubscribedUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/subscribed-users');
      setUsers(data.data);
    } catch (err) {
      setErrorMsg('Failed to load subscribed users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribedUsers();
  }, []);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const handleDelete = (id) => {
    clearMessages();
    Modal.confirm({
      title: 'Delete this subscribed user record?',
      onOk: async () => {
        try {
          await api.delete(`/subscribed-users/${id}`);
          setSuccessMsg('Subscribed user deleted successfully');
          fetchSubscribedUsers();
        } catch {
          setErrorMsg('Failed to delete subscribed user');
        }
      },
    });
  };

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      render: (_, __, index) => index + 1,
      width: 70,
    },
    { 
      title: 'User Name', 
      dataIndex: 'name', 
      key: 'name',
      ...getColumnSearchProps('name'),
    },
    { 
      title: 'Username', 
      dataIndex: 'username', 
      key: 'username',
      ...getColumnSearchProps('username'),
    },
    { 
      title: 'Email', 
      dataIndex: 'email', 
      key: 'email',
      ...getColumnSearchProps('email'),
    },
    { 
      title: 'Plan Name', 
      dataIndex: 'plan_name', 
      key: 'plan_name',
      ...getColumnSearchProps('plan_name'),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (value) => `₹${parseFloat(value).toFixed(2)}`,
      ...getColumnSearchProps('price'),
    },
    {
      title: 'Purchase Date',
      dataIndex: 'purchase_date',
      key: 'purchase_date',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : 'N/A',
      ...getColumnSearchProps('purchase_date'),
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : 'N/A',
      ...getColumnSearchProps('expiry_date'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const expired = dayjs(record.expiry_date).isBefore(dayjs());
        let color = status === 'active' ? 'green' : 'red';
        let label = expired ? 'Expired' : status === 'active' ? 'Active' : 'Inactive';
        return <Tag color={color}>{label}</Tag>;
      },
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Expired', value: 'expired' },
        { text: 'Inactive', value: 'inactive' },
      ],
      onFilter: (value, record) => {
        if (value === 'expired') {
          return dayjs(record.expiry_date).isBefore(dayjs());
        }
        if (value === 'active') {
          return record.status === 'active' && !dayjs(record.expiry_date).isBefore(dayjs());
        }
        return record.status === value;
      },
    },
    { 
      title: 'Transaction ID', 
      dataIndex: 'payment_transaction_id', 
      key: 'payment_transaction_id',
      ...getColumnSearchProps('payment_transaction_id'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" danger onClick={() => handleDelete(record.id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Paid User Management</title>
      </Helmet>

      <div style={{ padding: '20px' }}>
        <h2>Paid User Management</h2>

        {successMsg && (
          <Alert
            message={successMsg}
            type="success"
            showIcon
            closable
            onClose={() => setSuccessMsg('')}
            style={{ marginBottom: 12 }}
          />
        )}

        {errorMsg && (
          <Alert
            message={errorMsg}
            type="error"
            showIcon
            closable
            onClose={() => setErrorMsg('')}
            style={{ marginBottom: 12 }}
          />
        )}

        <Table
          rowKey="id"
          columns={columns}
          dataSource={users}
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />
      </div>
    </>
  );
};

export default SubscribedUserManagement;